import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Col } from "reactstrap";
import reactStringReplace from "react-string-replace";
import "components/custom.css";
// import { arrayMove } from "utils/arrayMove";
import ReactHtmlParser from "react-html-parser";
import WordDefinitionModal from 'components/PracticeMaterial/PopUps/WordDefinitionModal';
import FillInTheBlankExplanation from "components/PracticeMaterial/PopUps/FillInTheBlankExplanation";
import TimeUp from "components/PracticeMaterial/PopUps/TimeUp";

const IDEAL_TIME = 120; // SECONDS.

const WrittingBlank = (props) => {
  // Only keep essential state
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [answerData, setAnswerData] = useState({ answer: [], correct: [] });
  const [wordDefinitionModalIsOpen, setWordDefinitionModalIsOpen] = useState(false);
  const [modalDestroy, setModalDestroy] = useState(true);
  const [selectedWord, setSelectedWord] = useState("");
  const [explanationModal, setExplanationModal] = useState(false);
  const [idealTime, setIdealTime] = useState(IDEAL_TIME);
  const [idealTimeExceed, setIdealTimeExceed] = useState(false);
  const [answer_explaination_key, setAnswer_explaination_key] = useState("");

  // Removed unused state:
  // const [secondCount, setSecondCount] = useState(1200); // Unused in render
  // const [showTimer, setShowTimer] = useState(false); // Unused in render
  // const [resultObj, setResultObj] = useState({}); // Unused in render
  // const [board, setBoard] = useState({ lanes: [] }); // Unused
  // const [items, setItems] = useState([]); // Unused - can be derived
  // const [correctFlag, setCorrectFlag] = useState(true); // Logic handled in useEffect
  // const [isModalOpen, setIsModalOpen] = useState(false); // Unused modal
  // const [selectedValue, setSelectedValue] = useState(""); // Unused
  // const [timeoutStat, setTimeoutStat] = useState(false); // Unused

  // Refs
  const intervalIdRef = useRef(null);
  const hasCalledUpdateCorrect = useRef(false);
  const correctAnswersRef = useRef([]);

  // Memoized data
  const { data, answer, resultMode, testMode, showResponse, languages, option_explaination, submitDisable } = props;
  
  // Memoized derived values
  const questionText = useMemo(() => data?.question || "", [data]);
  
  const { blanks, formattedQuestion } = useMemo(() => {
    if (!data?.question) return { blanks: [], formattedQuestion: "" };
    
    const blanksArray = data.question
      .replace(/<[^>]*>?/gm, "x_x")
      .split("x_x")
      .filter((i, x) => x % 2 !== 0);
    
    const formattedQ = data.question.replace(/<[^>]*>?/gm, "x_x").replace(/x_x(.*?)x_x/g, "");
    
    return { blanks: blanksArray, formattedQuestion: formattedQ };
  }, [data]);

  const options = useMemo(() => data?.option || [], [data]);

  useEffect(() => {
    if (answer && intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, [answer]);

  // Initialize component and ideal timer
  useEffect(() => {
    // Start ideal time countdown if not in answer mode
    if (!answer) {
      intervalIdRef.current = setInterval(() => {
        setIdealTime(prev => {
          if (prev <= 1) {
            clearInterval(intervalIdRef.current);
            setIdealTimeExceed(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [answer]);

  // Handle question changes and resets
  useEffect(() => {
    if (!data) return;
    
    const isNewQuestion = questionId && questionId !== data.id;
    const shouldReset = isNewQuestion || props.restart;
    
    if (shouldReset) {
      // Clear existing interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      
      // Reset state
      setQuestionId(data.id);
      setReset(true);
      setAnswerData({ answer: [], correct: [] });
      setIdealTime(IDEAL_TIME);
      setIdealTimeExceed(false);
      hasCalledUpdateCorrect.current = false;
      
      // Clear input values
      document.querySelectorAll(".span11").forEach(t => {
        if (t.value !== undefined) t.value = "";
      });
      
      // Restart ideal timer if not in answer mode
      if (!answer) {
        intervalIdRef.current = setInterval(() => {
          setIdealTime(prev => {
            if (prev <= 1) {
              clearInterval(intervalIdRef.current);
              setIdealTimeExceed(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      
      // Call restartQue if restart prop is true
      if (props.restart && props.restartQue) {
        props.restartQue();
      }
    } else if (!questionId) {
      setQuestionId(data.id);
    }
  }, [data, props.restart, questionId, answer, props.restartQue]);

  // Submit correct answers when question changes
  useEffect(() => {
    if (!data || hasCalledUpdateCorrect.current) return;
    
    correctAnswersRef.current = blanks;
    
    if (props.submit) {
      props.submit({
        answer: [],
        correct: blanks
      });
    }
    
    hasCalledUpdateCorrect.current = true;
  }, [data, blanks, props.submit]);

  // Handle answer submission
  const changeSelect = useCallback((e) => {
    if (props.showResult) {
      props.showResult();
    }
    
    const index = e.target.id;
    const value = e.target.value;
    
    setAnswerData(prev => {
      const newAnswer = [...prev.answer];
      newAnswer[index] = value;
      
      const newData = {
        answer: newAnswer,
        correct: blanks
      };
      
      // Submit to parent
      if (props.submit) {
        props.submit(newData, true);
      }
      
      return {
        ...prev,
        ...newData
      };
    });
  }, [blanks, props.submit, props.showResult]);

  // Word definition modal handlers
  const openCloseWordDefinitionModal = useCallback((status, word = "") => {
    setWordDefinitionModalIsOpen(status);
    if (!status) {
      setTimeout(() => {
        setModalDestroy(true);
        setSelectedWord("");
      }, 500);
    } else {
      setModalDestroy(false);
      setSelectedWord(word);
    }
  }, []);

  const wordClickHandle = useCallback((e, word) => {
    e.preventDefault();
    openCloseWordDefinitionModal(true, word);
  }, [openCloseWordDefinitionModal]);

  // Explanation modal handlers
  const toggleModalExplanation = useCallback(() => {
    setExplanationModal(prev => !prev);
  }, []);

  const selectClickHandler = useCallback((correct, item) => {
    const userAnswer = answerData.answer[item]?.trim();
    if (userAnswer !== correct.trim()) {
      setAnswer_explaination_key(correct);
      toggleModalExplanation();
    }
  }, [answerData.answer, toggleModalExplanation]);

  // Answer rendering
  const renderAnswer = useCallback((answer_text, correct) => {
    if (answer_text === undefined || answer_text == "") {
      return ReactHtmlParser(`${correct} <span className="pull-right">&nbsp;?</span>`);
    } else if (answer_text.trim() === correct) {
      return answer_text;
    } else {
      // <span className="pull-right">&nbsp;?</span>
      return ReactHtmlParser(`${answer_text } <span className="pull-right">&nbsp;?</span>`);
    }
  }, []);

  // Answer status class calculation
  const answerStatusClass = useCallback((itemIndex, correct) => {
    if (!answer) return "";
    
    const userAnswer = answerData.answer[itemIndex]?.trim();
    console.log("userAnswer",userAnswer);
    
    if (userAnswer === undefined || userAnswer=="") {
      return "empty cursor-pointer clearfix"; // left blank by user
    } else if (userAnswer === correct.trim()) {
      return "correct"; // correct response
    } else {
      return "wrong q-mark cursor-pointer clearfix"; // wrong response
    }
  }, [answer, answerData.answer]);

  // Show correct answer alongside wrong one
  const showCorrectAlongWithWrong = useCallback((answer_text, correct) => {
    if (!answer || !answer_text || answer_text.trim() === correct.trim()) {
      return null;
    }
    
    return (
      <span className="wrong-answer-correction">
        <i className="fa fa-long-arrow-right"></i>
        <span className="span11 correct-inverse text-center">{correct.trim() ? correct.trim() : ''}</span>
      </span>
    );
  }, [answer]);

  // Main question rendering
  const renderQuestionParagraph = useCallback(() => {
    if (!data || !options.length) return null;

    const questionElements = reactStringReplace(
      formattedQuestion,
      /(__add_blank__)/g,
      (match, j) => {
        const blankIndex = (j - 1) / 2;
        const blankOptions = options[blankIndex]?.options?.split(",") || [];
        const correctAnswer = blanks[blankIndex] || "";

        return (
          <React.Fragment key={j}>
            {!answer ? (
              <select
                className="span11 text-black"
                id={j}
                onChange={changeSelect}
                value={answerData.answer[j] || ""}
              >
                <option value=""></option>
                {blankOptions.map((option, idx) => (
                  <option key={idx} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <div
                  className={`span11 ${answerStatusClass(j, correctAnswer)}`}
                  onClick={() => selectClickHandler(correctAnswer, j)}
                >
                  {renderAnswer(answerData.answer[j], correctAnswer).length !== 0 ? renderAnswer(answerData.answer[j], correctAnswer) : <span className="empty-space" />}
                </div>
                {showCorrectAlongWithWrong(answerData.answer[j], correctAnswer)}
              </>
            )}
          </React.Fragment>
        );
      }
    );

    // Format text with clickable words
    const formattedElements = [];
    questionElements.forEach((item, i) => {
      if (typeof item === "string") {
        item.split(" ").forEach((word, wordIdx) => {
          formattedElements.push(
            <span
              key={`${i}-${wordIdx}`}
              className="question-para-word speach-text select-word-hover-effect"
              onClick={(e) => wordClickHandle(e, word)}
            >
              {word}
            </span>
          );
          formattedElements.push(" ");
        });
      } else {
        formattedElements.push(
          <React.Fragment key={i}>{item}</React.Fragment>
        );
      }
    });

    return formattedElements;
  }, [
    data, options, formattedQuestion, blanks, answer, answerData.answer,
    changeSelect, answerStatusClass, renderAnswer, showCorrectAlongWithWrong,
    selectClickHandler, wordClickHandle
  ]);

  // Ideal time toggle
  const idealTimeExceedToggle = useCallback(() => {
    setIdealTimeExceed(prev => !prev);
  }, []);

  // Commented out unused functions:
  // const showAnswer = () => {
  //   // DOM manipulation - consider refactoring to React state if needed
  // };
  
  // const stopTest = () => {
  //   // Unused - can be implemented if needed
  // };
  
  // const onSortEnd = () => {
  //   // Unused sorting logic
  // };
  
  // const handleChange = () => {
  //   // Unused handler
  // };
  
  // const toggleModal = () => {
  //   // Unused modal
  // };

  return (
    <div className="visibility-visible">
      <div className="custom-box mx-0">
        <div className="ice-storm-form response question-text writing-blank">
          {renderQuestionParagraph()}
        </div>
      </div>

      {explanationModal && (
        <FillInTheBlankExplanation
          isOpen={explanationModal}
          title={`${answer_explaination_key.charAt(0).toUpperCase()}${answer_explaination_key.slice(1)}`}
          data={option_explaination}
          keyName={answer_explaination_key}
          closeModal={toggleModalExplanation}
        />
      )}

      <TimeUp
        isOpen={idealTimeExceed}
        toggle={idealTimeExceedToggle}
        title="Important !!!"
        message="You spent 2 minutes. This is the ideal time to attempt these questions. Click Submit Now to submit response or Close to ignore."
        buttonLabel="Submit Now"
        showResponse={showResponse}
        submitDisable={submitDisable}
      />

      {!resultMode && !testMode && !modalDestroy && (
        <Col xs={12}>
          <div className="row shadowing">
            <div className="word-definition">
              <WordDefinitionModal
                isOpen={wordDefinitionModalIsOpen}
                toggle={openCloseWordDefinitionModal}
                word={selectedWord}
                languages={languages}
              />
            </div>
          </div>
        </Col>
      )}
    </div>
  );
};

export default WrittingBlank;